export const parseObjects = <T>(
  value: string | T,
  onErrorCallback: (error: Error) => void = () => {},
): T => {
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch (error) {
      onErrorCallback(error);
      return {} as T;
    }
  }
  return value;
};

/**
 * Parse a given string to kebab-case.
 */
export const parseToKebabCase = (str: string) => {
  return str
    .replace(/([a-z])([A-Z0-9])/g, '$1-$2')
    .replace(/([0-9])([a-zA-Z])/g, '$1-$2')
    .replace(/[\s_]+/g, '-')
    .toLowerCase();
};

/**
 * Parse a kebab-case string to a normal (with spaces).
 */
export const parseFromKebabCase = (str: string) => {
  return str.replace(/-/g, ' ');
};

export const parseArrayToString = (
  arr: string[] | string | undefined | null,
  separator = ',',
): string => {
  if (arr == null) {
    return '';
  }

  if (typeof arr === 'string') {
    return arr;
  }

  return arr.join(separator);
};

export const parseStringToArray = (str: string | string[], separator = ',') => {
  if (Array.isArray(str)) {
    return str;
  }

  if (typeof str === 'string' && str.length === 0) {
    return [];
  }

  return str.split(separator);
};

/**
 * Check if a given string is a valid Base64 string.
 */
export const isBase64 = (str: string) => {
  if (typeof str !== 'string') {
    return false;
  }

  // Check if the string matches the Base64 pattern
  const base64Regex = new RegExp(
    '^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$',
  );
  // Ensure the string length is a multiple of 4 (valid for Base64)
  return base64Regex.test(str) && str.length % 4 === 0;
};

export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = error => reject(error);
  });
};

/**
 * Cleans the input string by trimming whitespace and converting it to lowercase.
 */
export const cleanCase = (string: string) => {
  return String(string || '')
    .trim()
    .toLowerCase();
};

/**
 * Converts a given string into space-separated words.
 *
 * Handles various cases such as camelCase, snake_case, dot.case, and more.
 * Non-alphanumeric characters (except spaces) are replaced with spaces.
 */
export const toSpaceCase = (string: string) => {
  return cleanCase(string)
    .replace(/[\W_]+(.|$)/g, (_matches, match) => {
      return match ? ' ' + match : '';
    })
    .trim();
};

/**
 * Converts a given string into snake_case format.
 *
 * Converts the input string into space-separated words (using `toSpaceCase`)
 * and then replaces all spaces with underscores.
 */
export const toSnakeCase = (string: string) => {
  return toSpaceCase(string).replace(/\s/g, '_');
};

export const toQueryString = <T extends object>(params: T): string =>
  new URLSearchParams(
    Object.entries(params)
      .filter(([, v]) => v != null)
      .map(([k, v]) => [k, String(v)])
  ).toString();
